import { apiClient } from './api/apiClient';

export interface BackupInfo {
  filename: string;
  size: number;
  createdAt: string;
  label?: string;
}

export interface BackupSettings {
  enabled: boolean;
  schedule: string;
  retainCount: number;
  lastRunAt?: string | null;
  lastRunStatus?: 'ok' | 'error' | null;
  lastRunError?: string | null;
}

class BackupService {
  async listBackups(): Promise<BackupInfo[]> {
    const res = await apiClient.get<BackupInfo[]>('/api/backup', true);
    return res.data ?? [];
  }

  async createBackup(label?: string): Promise<BackupInfo> {
    const res = await apiClient.post<BackupInfo>('/api/backup', { label: label || '' }, true);
    if (!res.data) throw new Error(res.error || 'Failed to create backup');
    return res.data;
  }

  async restoreBackup(filename: string): Promise<void> {
    const res = await apiClient.post(`/api/backup/${encodeURIComponent(filename)}/restore`, {}, true);
    if (res.error) throw new Error(res.error);
  }

  async deleteBackup(filename: string): Promise<void> {
    const res = await apiClient.delete(`/api/backup/${encodeURIComponent(filename)}`, true);
    if (res.error) throw new Error(res.error);
  }

  /** Returns a URL that, when opened, will stream-download the zip file. */
  getDownloadUrl(filename: string): string {
    // Use token as query param so a plain <a> link works
    const token = localStorage.getItem('accessToken') || '';
    const base = (window as any).__REACT_APP_API_URL__ || '';
    return `${base}/api/backup/${encodeURIComponent(filename)}/download?token=${encodeURIComponent(token)}`;
  }

  async getSettings(): Promise<BackupSettings> {
    const res = await apiClient.get<BackupSettings>('/api/backup/settings', true);
    return res.data!;
  }

  uploadBackup(file: File, onProgress?: (pct: number) => void): Promise<BackupInfo> {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('accessToken') || '';
      const base = (window as any).__REACT_APP_API_URL__ || '';
      const formData = new FormData();
      formData.append('backup', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${base}/api/backup/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        };
      }

      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 400 || data.error) {
            return reject(new Error(data.error || `HTTP ${xhr.status}`));
          }
          resolve(data.data as BackupInfo);
        } catch (e) { reject(e); }
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(formData);
    });
  }

  async updateSettings(patch: Partial<BackupSettings>): Promise<BackupSettings> {
    const res = await apiClient.patch<BackupSettings>('/api/backup/settings', patch as any, true);
    if (!res.data) throw new Error(res.error || 'Failed to update settings');
    return res.data;
  }
}

export default new BackupService();
